import { ProgressDetailKey, ProgressStage } from "@/graphql/generated/types";
import type { Msg } from "@/i18n/Msg";

// genesis sends progress as keys and numbers, never as sentences (the side channel's contract); this
// module is the one place that words them. The maps are total over the generated enums, so a stage or
// fact genesis adds breaks the typecheck here until it has a label. A key this build does not know yet
// (genesis deployed first) falls back to the generic label instead of showing the raw key.

const STAGE_LABELS: Record<ProgressStage, Msg> = {
  [ProgressStage.MatchPrepare]: { id: "progress.stage.matchPrepare", defaultMessage: "Prepare" },
  [ProgressStage.MatchCriteria]: { id: "progress.stage.matchCriteria", defaultMessage: "Check criteria" },
  [ProgressStage.MatchAssessment]: { id: "progress.stage.matchAssessment", defaultMessage: "Assess candidates" },
  [ProgressStage.MatchSolve]: { id: "progress.stage.matchSolve", defaultMessage: "Compose teams" },
  [ProgressStage.MatchFinalize]: { id: "progress.stage.matchFinalize", defaultMessage: "Create result" },
};

// Only stages that count carry a counter line; the rest show their state alone.
const STAGE_COUNTERS: Partial<Record<ProgressStage, Msg>> = {
  [ProgressStage.MatchCriteria]: { id: "progress.counter.matchCriteria", defaultMessage: "{done} of {total} checks" },
  [ProgressStage.MatchAssessment]: {
    id: "progress.counter.matchAssessment",
    defaultMessage: "{done} of {total} assessments finished",
  },
};

const DETAIL_LABELS: Record<ProgressDetailKey, Msg> = {
  [ProgressDetailKey.MatchCompaniesLoaded]: {
    id: "progress.detail.matchCompaniesLoaded",
    defaultMessage: "{count, plural, one {# company} other {# companies}} loaded",
  },
  [ProgressDetailKey.MatchCandidatesLoaded]: {
    id: "progress.detail.matchCandidatesLoaded",
    defaultMessage: "{count, plural, one {# candidate} other {# candidates}} loaded",
  },
  [ProgressDetailKey.MatchCriteriaActive]: {
    id: "progress.detail.matchCriteriaActive",
    defaultMessage: "{count, plural, one {# criterion} other {# criteria}} active",
  },
  [ProgressDetailKey.MatchLocationsResolved]: {
    id: "progress.detail.matchLocationsResolved",
    defaultMessage: "{count} of {total} residences located",
  },
  [ProgressDetailKey.MatchChecksWithAi]: {
    id: "progress.detail.matchChecksWithAi",
    defaultMessage: "{count} of them with AI",
  },
  [ProgressDetailKey.MatchAssessmentsFailed]: {
    id: "progress.detail.matchAssessmentsFailed",
    defaultMessage: "{count} of them failed",
  },
};

export const PROGRESS_MESSAGES = {
  unknownStage: { id: "progress.stage.unknown", defaultMessage: "Working" },
  counter: { id: "progress.counter.generic", defaultMessage: "{done} of {total}" },
  waiting: { id: "progress.waiting", defaultMessage: "Starting …" },
  label: { id: "progress.label", defaultMessage: "Progress" },
  elapsedSeconds: { id: "progress.elapsedSeconds", defaultMessage: "{seconds} s" },
  elapsedMinutes: { id: "progress.elapsedMinutes", defaultMessage: "{minutes} min {seconds} s" },
} as const satisfies Record<string, Msg>;

function isKnown<T extends string>(values: Record<T, unknown>, key: string): key is T {
  return Object.hasOwn(values, key);
}

export function stageLabel(key: string): Msg {
  return isKnown(STAGE_LABELS, key) ? STAGE_LABELS[key] : PROGRESS_MESSAGES.unknownStage;
}

export function stageCounter(key: string): Msg {
  return (isKnown(STAGE_LABELS, key) ? STAGE_COUNTERS[key] : undefined) ?? PROGRESS_MESSAGES.counter;
}

// null for a fact this build cannot word: an unknown fact is dropped, not shown as a raw key.
export function detailLabel(key: string): Msg | null {
  return isKnown(DETAIL_LABELS, key) ? DETAIL_LABELS[key] : null;
}
